<script setup lang="ts">
import { computed, resolveComponent, type ConcreteComponent } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import type { Target } from '../../../types'
import { LinkTarget } from '../../enums/link-target'

type Variant = 'clear' | 'clear-light' | 'light' | 'outlined' | 'outlined-dark' | 'primary' | 'secondary'
type Type = 'button' | 'submit' | 'reset'
type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

interface Props {
  /**
   * Add an appended icon if set
   * @string
   */
  appendedIcon?: string
  /**
   * Hide label if set
   * @boolean
   * @default {false}
   */
  /**
   * If set then the button is link
   */
  href?: string
  /**
   * If set the label is only for screen readers
   */
  isLabelHidden?: boolean
  /**
   * If the button is only for presentation
   */
  isForPresentation?: boolean
  /**
   * Size of the button.
   * @enum {Size} `xs` sm` `md` `lg` `xl`
   * @default md
   */
  /**
   * Add a prepended icon if set
   * @string
   */
  prependedIcon?: string
  size?: Size
  /**
   * If set then the button is link
   */
  target?: Maybe<Target>
  to?: RouteLocationRaw
  /**
   * Type of the button.
   * @enum {Type} `button` `submit' `reset`
   * @default button
   */
  type?: Type
  /**
   * Variant of the button
   * @enum {Variant} `clear` `clear-light` `outlined` `outlined-dark` `primary` `secondary`
   * @default undefined
   */
  variant?: Variant
}

const props = withDefaults(defineProps<Props>(), {
  prependedIcon: undefined,
  appendedIcon: undefined,
  href: undefined,
  size: 'md',
  target: LinkTarget.SELF,
  to: undefined,
  type: 'button',
  variant: undefined,
})

const componentName = computed<ConcreteComponent | string>(() => {
  if (props.href || props.to) {
    return resolveComponent('NuxtLink')
  }

  if (props.isForPresentation) {
    return 'span'
  }

  return 'button'
})

const target = computed<Target>(() => props.target || LinkTarget.SELF)

const componentAttrs = computed<Record<string, unknown>>(() => {
  if (props.href) {
    return {
      target: target.value,
      to: props.href,
    }
  }

  if (props.to) {
    return {
      target: target.value,
      to: props.to,
    }
  }

  return {
    type: props.type,
  }
})
</script>

<template>
  <Component
    :is="componentName"
    v-bind="{
      ...componentAttrs,
      ...$attrs,
    }"
    class="button"
    :class="{
      [`button--${variant}`]: variant,
      [`button--${size}`]: size,
      [`button--has-icon`]: prependedIcon || appendedIcon,
    }"
  >
    <Icon
      v-if="prependedIcon"
      :name="prependedIcon"
      role="presentation"
      class="button__icon"
    />
    <span :class="{ 'sr-only': isLabelHidden }">
      <slot />
    </span>
    <Icon
      v-if="appendedIcon"
      :name="appendedIcon"
      role="presentation"
      class="button__icon"
    />
  </Component>
</template>
